import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  HeartIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  LanguageIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const navigation = [
  { name: 'dashboard', href: '/', icon: HomeIcon },
  { name: 'appointments', href: '/appointments', icon: CalendarDaysIcon },
  { name: 'patients', href: '/patients', icon: UsersIcon },
  { name: 'professionals', href: '/professionals', icon: UserGroupIcon },
  { name: 'treatments', href: '/treatments', icon: HeartIcon },
  { name: 'specialties', href: '/specialties', icon: AcademicCapIcon },
  { name: 'statistics', href: '/statistics', icon: ChartBarIcon },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar móvil */}
      <div className={clsx(
        'fixed inset-0 z-40 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="absolute inset-0 bg-gray-600 opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-64 flex-col bg-white dark:bg-gray-800 h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Clínica SG
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <SidebarContent />
        </div>
      </div>

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Clínica Santiago Gómez
            </h1>
          </div>
          <SidebarContent />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Sección izquierda - Menú móvil y título */}
            <div className="flex items-center space-x-4 flex-1">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
              
              <div className="hidden lg:block">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('navigation.dashboard')}
                </h2>
              </div>
            </div>

            {/* Sección central - Espacio flexible para futuro contenido */}
            <div className="hidden md:flex flex-1 justify-center">
              {/* Aquí se puede agregar breadcrumbs o contenido central en el futuro */}
            </div>
            
            {/* Sección derecha - Controles */}
            <div className="flex items-center space-x-2 flex-1 justify-end">
              {/* Selector de idioma */}
              <button
                onClick={toggleLanguage}
                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-md transition-colors"
                title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
              >
                <LanguageIcon className="w-5 h-5" />
                <span className="ml-1 text-sm font-medium">
                  {i18n.language.toUpperCase()}
                </span>
              </button>

              {/* Toggle tema */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-md transition-colors"
                title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
              >
                {theme === 'light' ? (
                  <MoonIcon className="w-5 h-5" />
                ) : (
                  <SunIcon className="w-5 h-5" />
                )}
              </button>

              {/* Separador visual */}
              <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 mx-2"></div>

              {/* Info del usuario */}
              <div className="flex items-center space-x-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.rol}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-md transition-colors"
                  title={t('navigation.logout')}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Contenido */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );

  function SidebarContent() {
    return (
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              clsx(
                'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
              )
            }
          >
            <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
            {t(`navigation.${item.name}`)}
          </NavLink>
        ))}
      </nav>
    );
  }
} 